import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "../../../components/Buttons/Button";
import { ConfirmAlert, SimpleAlert } from "../../../components/Alerts/Alerts";
import { useTranslationApp } from "../../../lib/i18next";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import {
  BUTTON_GO_BACK,
  CAMPAIGN_APPROVAL_APPROVE_BUTTON,
  CAMPAIGN_APPROVAL_APPROVE_CAMPAIGN_BUTTON,
  CAMPAIGN_APPROVAL_COMMENTS_LEFT,
  CAMPAIGN_APPROVAL_CONFIRM_APPROVE_DESCRIPTION,
  CAMPAIGN_APPROVAL_CONFIRM_APPROVE_TITLE,
  CAMPAIGN_APPROVAL_DECISION_APPROVED_SUCCESS,
  CAMPAIGN_APPROVAL_READY_TO_DECIDE,
  OK,
} from "../../../i18n/keysTranslations";
import { ALERT_ICON_TYPE_SUCCESS } from "../../../utils/constants";
import {
  campaignApprovalMainContentSx,
  campaignApprovalMainPaddingSx,
} from "../utils";

const DecideBar = ({
  disabled = false,
  isApproveDisabled = false,
  comments = [],
  currentDraftID = "",
  onSubmitDecision,
}) => {
  const { t } = useTranslationApp();
  const [pendingDecision, setPendingDecision] = useState(null);

  const commentsCount = comments.filter(
    (comment) =>
      !comment?.isStale &&
      (comment?.type === "general" || comment?.type === "specific") &&
      (!currentDraftID ||
        !comment?.draftID ||
        comment?.draftID === currentDraftID)
  ).length;

  const onHandleApprove = async () => {
    const confirmResult = await ConfirmAlert({
      title: t(CAMPAIGN_APPROVAL_CONFIRM_APPROVE_TITLE),
      text: t(CAMPAIGN_APPROVAL_CONFIRM_APPROVE_DESCRIPTION),
      confirmButtonText: t(CAMPAIGN_APPROVAL_APPROVE_BUTTON),
      cancelButtonText: t(BUTTON_GO_BACK),
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setPendingDecision("approved");

    try {
      const response = await onSubmitDecision?.({
        decision: "approved",
        body: "",
      });

      if (response?.ok) {
        await SimpleAlert({
          title: t(CAMPAIGN_APPROVAL_DECISION_APPROVED_SUCCESS),
          icon: ALERT_ICON_TYPE_SUCCESS,
          confirmButtonText: t(OK),
        });
      }
    } finally {
      setPendingDecision(null);
    }
  };

  const isSubmitting = pendingDecision !== null;
  const isInteractionBlocked = disabled || isApproveDisabled || isSubmitting;
  const isApproveLoading = pendingDecision === "approved";

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
        bgcolor: "background.paper",
        py: { xs: 1.5, md: 2 },
      }}
    >
      <Box sx={campaignApprovalMainPaddingSx}>
        <Box
          sx={{
            ...campaignApprovalMainContentSx,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t(CAMPAIGN_APPROVAL_READY_TO_DECIDE)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25 }}
            >
              {t(CAMPAIGN_APPROVAL_COMMENTS_LEFT, { count: commentsCount })}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: { xs: "stretch", sm: "flex-end" },
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <Button
              type="button"
              disabled={isInteractionBlocked}
              loading={isApproveLoading}
              onClick={onHandleApprove}
              sx={{ minWidth: { xs: "100%", sm: 140 } }}
            >
              {t(CAMPAIGN_APPROVAL_APPROVE_CAMPAIGN_BUTTON)}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DecideBar;
